import { z } from 'zod/v3';

import { marketECouponInfoSchema } from './e-coupon';
import type { FrequentlyOrderProduct } from './frequently-order';
import {
  deliveryFeeTypeSchema,
  deliveryFlagSchema,
  frequentlyOrderProductSchema,
  marketProductTypeSchema,
  productDeliveryTypeSchema,
  productStatusSchema,
  salesTargetSchema,
  validationResponseSchema,
} from './frequently-order';

/**
 * 마켓 상품 (MarketSelectProductResponseV2)
 * frequently-order 의 상품 모델과 동일 DTO 이므로 재사용한다.
 * (유사상품 / 함께 구매하는 상품 등 상품 리스트 응답에 사용)
 */
export const marketSelectProductSchema = frequentlyOrderProductSchema;
export type MarketSelectProduct = FrequentlyOrderProduct;

// ============================================================================
// 최근 본 상품 (MarketRecentlyViewedProductResponse = MarketSelectProductResponseV2 + 배송 텍스트)
// ============================================================================
export const recentlyViewedProductSchema = frequentlyOrderProductSchema.extend({
  /** 배송 예상 도착 텍스트 (예: 오늘(수) 도착) */
  expectedDeliveryText: z.string().optional(),
  /** 배송 마감 시간 (ISO-8601) */
  lastDeliveryCutoff: z.string().optional(),
});
export type RecentlyViewedProduct = z.infer<typeof recentlyViewedProductSchema>;

// ============================================================================
// 상품 옵션 (ProductOptionListResponse / MarketProductOptionDetailsResponse)
// ============================================================================
/** SKU 상태 */
export const optionSkuStatusSchema = z.enum(['NONE', 'NORMAL', 'DELETED']);
export type OptionSkuStatus = z.infer<typeof optionSkuStatusSchema>;

/** 옵션 노출 상태 */
export const optionStatusSchema = z.enum([
  'DISPLAYING',
  'HIDED',
  'OUT_OF_STOCK',
  'NONEXISTENT',
]);
export type OptionStatus = z.infer<typeof optionStatusSchema>;

export const productOptionDetailSchema = z.object({
  /** 계산된 최소 주문 수량 */
  calcMinimumOrderQuantity: z.number().optional(),
  /** 캐시노트 플러스 적용 판매단가 */
  cashnotePlusDiscountedPrice: z.number().optional(),
  /** SKU Code */
  code: z.string().optional(),
  /** 기본 옵션 여부 */
  default: z.boolean().optional(),
  /** 수량별 배송비 부과 단위 */
  deliveryFeePerQuantity: z.number().optional(),
  /** 대표 할인율 (%) */
  discountRate: z.number().optional(),
  /** 개별 주문 수량 */
  individualOrderQuantity: z.number().optional(),
  /** 개별 수량 제한 */
  individualQuantityLimit: z.number().optional(),
  /** 개별 수량 제한 사용 여부 */
  individualQuantityLimitEnabled: z.boolean().optional(),
  /** 기본 옵션 여부 */
  isDefault: z.boolean().optional(),
  /** 개별 수량 제한 사용 여부 */
  isIndividualQuantityLimitEnabled: z.boolean().optional(),
  /** 최소 주문 수량 사용 여부 */
  isMinimumOrderQuantityEnabled: z.boolean().optional(),
  /** 최소 주문 수량 */
  minimumOrderQuantity: z.number().optional(),
  /** 최소 주문 수량 사용 여부 */
  minimumOrderQuantityEnabled: z.boolean().optional(),
  /** 옵션명 */
  name: z.string().optional(),
  /** 정렬 순서 */
  orderNumber: z.number().optional(),
  /** 배수별 주문 단위 */
  orderPerMultipleQuantity: z.number(),
  /** 주문 가능 수량 */
  orderableQuantity: z.number().optional(),
  /** 판매단가 */
  price: z.number(),
  /** 할인 전 단가 */
  priceBeforeDiscount: z.number().optional(),
  /** 가격 차이 */
  priceDifference: z.number(),
  /** 가격 차이 텍스트 */
  priceDifferenceText: z.string(),
  /** 수량 */
  quantity: z.number(),
  /** SKU ID */
  skuId: z.number().optional(),
  /** SKU 상태 */
  skuStatus: optionSkuStatusSchema.optional(),
  /** 옵션 노출 상태 */
  status: optionStatusSchema.optional(),
});
export type ProductOptionDetail = z.infer<typeof productOptionDetailSchema>;

export const productOptionListSchema = z.object({
  /** 입점몰명 */
  mallName: z.string().optional(),
  /** 오퍼 ID */
  offerId: z.number().optional(),
  /** 옵션 정보 목록 */
  options: z.array(productOptionDetailSchema),
  /** 상품명 */
  productName: z.string().optional(),
});
export type ProductOptionList = z.infer<typeof productOptionListSchema>;

// ============================================================================
// 결제 예상 금액 (EstimateProductPaymentResponse / DiscountAmountCalculateResult)
// ============================================================================
/** 할인 계산 방식 */
export const discountModeSchema = z.enum(['FIXED', 'PERCENT']);
export type DiscountMode = z.infer<typeof discountModeSchema>;

/** 할인 종류 */
export const estimateDiscountTypeSchema = z.enum([
  'COUPON',
  'MEMBERSHIP_EVENT',
  'MEMBERSHIP_OPTION_LUCKY_BONUS_PACK',
]);
export type EstimateDiscountType = z.infer<typeof estimateDiscountTypeSchema>;

export const estimateDiscountResultSchema = z.object({
  /** 계산된 할인 금액 */
  calcDiscountAmount: z.number(),
  /** 할인 설명 */
  discountDescription: z.string(),
  /** 할인 계산 방식 */
  discountMode: discountModeSchema,
  /** 할인율 */
  discountRatio: z.number().optional(),
  /** 할인 종류 */
  discountType: estimateDiscountTypeSchema,
  /** 멤버십 할인 여부 */
  isMembershipDiscount: z.boolean(),
  /** 최대 할인 금액 */
  maximumDiscountAmount: z.number().optional(),
  /** 할인명 */
  name: z.string().optional(),
  /** 오퍼 ID */
  offerId: z.number(),
  /** 참조 ID */
  referenceId: z.number().optional(),
});
export type EstimateDiscountResult = z.infer<
  typeof estimateDiscountResultSchema
>;

export const estimateProductPaymentSchema = z.object({
  /** 쿠폰 할인 */
  discountCoupon: estimateDiscountResultSchema.optional(),
  /** 멤버십 할인 */
  discountMembershipDiscount: estimateDiscountResultSchema.optional(),
  /** 멤버십 옵션 할인 */
  discountMembershipOption: estimateDiscountResultSchema.optional(),
  /** 총 할인 금액 */
  discountPrice: z.number().optional(),
  /** 예상 결제 금액 */
  estimatePrice: z.number(),
  /** 총 금액 */
  totalPrice: z.number(),
  /** 총 수량 */
  totalQuantity: z.number(),
});
export type EstimateProductPayment = z.infer<
  typeof estimateProductPaymentSchema
>;

// ============================================================================
// 상품 상세 (MarketProductDetailsResponseV2)
// ============================================================================
/** 상세 이미지 링크 타입 */
export const marketContentImageLinkTypeSchema = z.enum([
  'NONE',
  'INTERNAL_PRODUCT',
  'INTERNAL_SHOWCASE',
  'EXTERNAL',
]);
export type MarketContentImageLinkType = z.infer<
  typeof marketContentImageLinkTypeSchema
>;

/** 상품 상세 이미지 (링크 연결 정보 포함) */
export const marketContentImageSchema = z.object({
  /** 이미지 URL */
  url: z.string(),
  /** 링크 타입 */
  linkType: marketContentImageLinkTypeSchema,
  /** 외부 링크 URL */
  linkUrl: z.string().optional(),
  /** 연결 상품 오퍼 ID */
  linkOfferId: z.number().optional(),
  /** 연결 상품 입점몰 ID */
  linkOfferVendorId: z.number().optional(),
  /** 연결 쇼케이스 코드 */
  linkShowcaseCode: z.string().optional(),
});
export type MarketContentImage = z.infer<typeof marketContentImageSchema>;

/** 입점사 정보 */
export const marketProductVendorSchema = z
  .object({
    /** 입점사 ID */
    id: z.number().optional(),
    /** 입점몰명 */
    mallName: z.string().optional(),
    /** 주소 */
    address: z.string().optional(),
    /** 상세 주소 */
    detailAddress: z.string().optional(),
    /** AS 연락처 */
    asContact: z.string().optional(),
    /** 사업자번호 */
    businessNumber: z.string().optional(),
    /** 통신판매업 신고번호 */
    mailOrderNumber: z.string().optional(),
  })
  .passthrough();
export type MarketProductVendor = z.infer<typeof marketProductVendorSchema>;

/**
 * 복잡 중첩 객체는 현재 lenient(passthrough)로 두고, 필요 시 정식 모델로 확장한다.
 * (banners / deliveryPolicies / discountResult / productNotices /
 *  returnExchangePolicies / extraInfo 등)
 */
const looseObjectSchema = z.object({}).passthrough();

export const marketProductDetailSchema = z
  .object({
    /** 오퍼 ID */
    offerId: z.number().optional(),
    /** 상품명 */
    productName: z.string().optional(),
    /** 상품코드 */
    productCode: z.string().optional(),
    /** 상품 타입 */
    productType: marketProductTypeSchema,
    /** 상품 상태 */
    status: productStatusSchema.optional(),
    /** 판매단가 */
    price: z.number().optional(),
    /** 할인 전 단가 */
    priceBeforeDiscount: z.number().optional(),
    /** 쿠폰 적용 판매단가 */
    couponDiscountedPrice: z.number().optional(),
    /** 캐시노트 플러스 적용 판매단가 */
    cashnotePlusDiscountedPrice: z.number().optional(),
    /** 대표 할인율 (%) */
    discountRate: z.number().optional(),
    /** 총 할인 금액 */
    totalDiscountAmount: z.number().optional(),
    /** 총 할인 적용가 */
    totalDiscountedPrice: z.number().optional(),
    /** 대표이미지 */
    mainImages: z.array(z.string()),
    /** 상세 이미지 목록 */
    contentImages: z.array(marketContentImageSchema),
    /** 옵션 정보 목록 */
    options: z.array(productOptionDetailSchema),
    /** 주문 가능 여부 */
    orderable: validationResponseSchema,
    /** 카테고리 ID 목록 */
    categoryIds: z.array(z.number()).optional(),
    /** 대표 배송형태 */
    deliveryType: productDeliveryTypeSchema.optional(),
    /** 대표 배송비 산정방식 */
    deliveryFeeType: deliveryFeeTypeSchema.optional(),
    /** 대표 배송비 */
    deliveryFee: z.number().optional(),
    /** 배송 플래그 목록 */
    deliveryFlags: z.array(deliveryFlagSchema).optional(),
    /** 묶음배송 가능 여부 */
    canBeBundled: z.boolean().optional(),
    /** 간편 주문 가능 여부 */
    canBeSimpleOrder: z.boolean(),
    /** 무료배송 여부 */
    isFreeShipping: z.boolean(),
    /** 특가 여부 */
    isHotDeal: z.boolean(),
    /** 선물하기 가능 여부 */
    isGift: z.boolean(),
    /** 도착 예정 일자 텍스트 */
    expectedDeliveryText: z.string().optional(),
    /** 배송 마감 시간 */
    lastDeliveryCutoff: z.string().optional(),
    /** 예상 적립 포인트 */
    expectedPoints: z.number(),
    /** E쿠폰 상품정보 */
    eCouponInfo: marketECouponInfoSchema.optional(),
    /** E쿠폰 주문버튼 비활성화 여부 */
    eCouponOrderDisabled: z.boolean(),
    /** E쿠폰 주문불가 라벨 */
    eCouponOrderDisabledLabel: z.string().optional(),
    /** 프로모션 텍스트 */
    promotionText: z.string().optional(),
    /** 전용 상품 목록 */
    salesTargets: z.array(salesTargetSchema).optional(),
    /** 적용된 쿠폰 할인 */
    discountCoupon: estimateDiscountResultSchema.optional(),
    /** 적용된 멤버십 이벤트 할인 */
    discountMembershipEvent: estimateDiscountResultSchema.optional(),
    /** 최대 할인 쿠폰 ID */
    maxDiscountCouponId: z.number().optional(),
    /** 캐시노트 플러스 배너 노출 여부 */
    showCashnotePlusBanner: z.boolean(),
    /** 캐시노트 플러스 가입 권유 노출 여부 */
    showCashnotePlusSuggestion: z.boolean(),
    /** 입점사 정보 */
    vendor: marketProductVendorSchema.optional(),
    /** 반품/교환 정책 설정 타입 */
    returnExchangePolicySettingType: z
      .enum(['NONE', 'SINGLE', 'DIFFERENT'])
      .optional(),
    /** 상품 썸네일 하단 배너 (lenient) */
    banners: z.array(looseObjectSchema),
    /** 배송정책 정보 리스트 (lenient) */
    deliveryPolicies: z.array(looseObjectSchema),
    /** 할인 항목 (lenient) */
    discountResult: looseObjectSchema,
    /** 상품 고시 정보 (lenient) */
    productNotices: z.array(looseObjectSchema),
    /** 반품/교환 정책 목록 (lenient) */
    returnExchangePolicies: z.array(looseObjectSchema),
    /** 추가 정보 (lenient) */
    extraInfo: looseObjectSchema.optional(),
  })
  .passthrough();
export type MarketProductDetail = z.infer<typeof marketProductDetailSchema>;
